import { useRoute, useRouter } from 'vue-router'
import type { LocationQuery, RouteLocationNormalizedLoaded, RouteParams, Router } from 'vue-router'
import type { RouteName, Tab } from '@/router'
import { useLoginStore } from '@/stores/login'

/**
 * How a tap on a tab is written into the history.
 *
 * «Что брать» is home (MOL-128; «Поход» was, MOL-17). Leaving it pushes, moving between the other
 * sections replaces, and coming back to it is a step back. So the system «back» on Android walks
 * «Оценки → Что брать → out of the app» however many tabs were tapped in between, which is what
 * Android apps do, and an iPhone, with no such button, sees no difference.
 *
 * `top` is a tap on the section already open: iOS scrolls it to the top, and nothing in a
 * browser stands in the way of doing the same.
 */
export type TabMove = 'push' | 'replace' | 'back' | 'top'

/** The section the app opens on and «back» ends at. */
export const HOME = 'advice' satisfies Tab

export function tabMove(from: Tab | undefined, to: Tab, below: RouteName | undefined): TabMove {
  if (from === to) return 'top'
  if (to === HOME) return below === HOME ? 'back' : 'replace'
  return from === HOME ? 'push' : 'replace'
}

/**
 * Where the back chevron leads, and so what it is called: one step back onto the entry
 * underneath when that entry is one of this screen's ancestors — the very step the system button
 * takes, so the two never disagree — and otherwise a replace onto the parent, so the chevron
 * never leads out of the app.
 *
 * Any ancestor, not only the parent (MOL-77, owner's decision): a screen opened from further up
 * than its parent leads back to where it was opened from, not to a parent the system button
 * would walk past anyway.
 * Opened cold, the chain is laid underneath (`settleColdStart`) and the parent is below it.
 */
export interface BackTarget {
  readonly location: ReturnType<Router['resolve']>
  readonly step: boolean
  /** What the chevron is called where the screen it lands on is not what is drawn there: the door. */
  readonly titleKey?: string
}

/**
 * `door` — the login stands in front of the app (`login.closed`). A public screen behind it leads
 * back to the door, not to its parent (MOL-216, Е-9): «‹ Настройки» promised a screen nobody is let
 * into, and `App.vue` drew the login at `/settings`.
 */
export function backTarget(router: Router, route: Routed, door = false): BackTarget | null {
  if (door && route.meta.public) return doorTarget(router)
  const parent = parentOf(router, route)
  if (!parent) return null
  const below: unknown = router.options.history.state.back
  const path = typeof below === 'string' ? router.resolve(below).path : null
  for (let ancestor: typeof parent | null = parent; ancestor; ancestor = parentOf(router, ancestor))
    if (ancestor.path === path) return { location: ancestor, step: true }
  return { location: parent, step: false }
}

/**
 * Back to the door: a step onto the entry underneath — the login, at whatever address the person was
 * going to, so they arrive there once in (MOL-56) — and with nothing of ours underneath, a replace onto
 * home, where the door is drawn. Opened cold behind the door, home is what `settleColdStart` lays
 * underneath (owner's В-1 «а»). An entry underneath that is public itself is drawn as itself, and is
 * named so.
 */
function doorTarget(router: Router): BackTarget {
  const below: unknown = router.options.history.state.back
  if (typeof below !== 'string')
    return { location: router.resolve({ name: HOME }), step: false, titleKey: DOOR_TITLE }
  const location = router.resolve(below)
  return location.meta.public
    ? { location, step: true }
    : { location, step: true, titleKey: DOOR_TITLE }
}

const DOOR_TITLE = 'login.title'

/**
 * Onto the parent itself, never past it to an older ancestor: a step back when the parent is the
 * entry underneath, otherwise a replace onto it. For a screen that is gone and whose parent has
 * something to say about it — a deleted account, whose «Вернуть» stands on «Счета» (MOL-123,
 * review 32): opened from a line of the card «Деньги» had until MOL-159, the chevron's step went to
 * «Деньги», where nothing offered it back — and a link from anywhere else would do the same.
 */
export function upTarget(router: Router, route: Routed): BackTarget | null {
  const parent = parentOf(router, route)
  if (!parent) return null
  const below: unknown = router.options.history.state.back
  const path = typeof below === 'string' ? router.resolve(below).path : null
  return { location: parent, step: parent.path === path }
}

/**
 * Which screen the entry underneath the current one is — by route, never by address. The
 * history records the address whole, and `/?utm_source=telegram` or `/#top` is home as much as
 * `/` is; a string compared with `'/'` took them for somewhere else, and «back» from home then
 * led home again.
 */
function entryBelow(router: Router): RouteName | undefined {
  const back: unknown = router.options.history.state.back
  if (typeof back !== 'string') return undefined
  const name = router.resolve(back).name
  return typeof name === 'string' ? (name as RouteName) : undefined
}

/** The section a screen belongs to: its own tab, or the nearest ancestor's. */
function sectionOf(router: Router, route: Routed & { meta: { tab?: Tab } }): Tab | undefined {
  if (route.meta.tab) return route.meta.tab
  for (let up = parentOf(router, route); up; up = parentOf(router, up))
    if (up.meta.tab) return up.meta.tab
  return undefined
}

/**
 * Where a parent is, with the parameters it needs. Taken from the route below rather than
 * listed: this used to be spelled out in `settleColdStart` and in `goBack` both, and the next
 * parameterised parent would have had to be added twice — a place forgotten there breaks «назад»
 * with nothing on screen to say so (З-9).
 */
function parentOf(
  router: Router,
  route: Pick<Routed, 'meta' | 'params' | 'query'>,
): ReturnType<Router['resolve']> | null {
  const parent = parentName(route)
  if (!parent) return null
  // Only the parameters the parent names: an account's id handed to «Счета» is discarded with a
  // warning on every draw of the chevron.
  const path = router.getRoutes().find((record) => record.name === parent)?.path ?? ''
  const named = new Set(Array.from(path.matchAll(/:(\w+)/g), ([, key]) => key))
  const params = Object.fromEntries(Object.entries(route.params).filter(([key]) => named.has(key)))
  return router.resolve({ name: parent, params })
}

/**
 * The parent a screen names: its own, or the one `?from=` names when the screen allows it
 * (MOL-82, В-3) — a finished trip opened from «Деньги» leads back there, «‹ Деньги», and the chevron
 * and the system «back» go to the same month. Only a name the route lists: an address must not be
 * able to make any screen the parent of any other.
 */
function parentName(route: Pick<Routed, 'meta' | 'query'>): RouteName | undefined {
  const from = route.query?.from
  if (typeof from === 'string' && route.meta.from?.includes(from as RouteName))
    return from as RouteName
  return route.meta.parent
}

interface Routed {
  meta: { parent?: RouteName; from?: readonly RouteName[]; public?: boolean }
  params: RouteParams
  query?: LocationQuery
}

/**
 * A nested screen opened cold — a reload, a tab the phone unloaded and restored, a link — has
 * nothing of ours underneath, and the system «back» would leave the app while the chevron
 * promises the parent. The parent is laid underneath, out of sight, before the first paint:
 * one replace onto it, one push back onto the screen.
 */
export async function settleColdStart(router: Router, door = () => false): Promise<void> {
  await router.isReady()
  const route = router.currentRoute.value
  const parent = parentName(route)
  if (!parent || router.options.history.state.back) return

  // A public screen behind the door leads back to it, and the door stands at home (MOL-216, В-1 «а»):
  // with «Настройки» underneath, a stranger who read the privacy page signed in onto the settings.
  if (route.meta.public && door()) {
    await router.replace({ name: HOME })
    await router.push(route.fullPath)
    return
  }

  const ancestors: string[] = []
  let cursor = router.resolve(route.fullPath)
  let ancestor = parentOf(router, cursor)
  while (ancestor) {
    ancestors.unshift(ancestor.fullPath)
    cursor = ancestor
    ancestor = parentOf(router, cursor)
  }
  const root = ancestors.shift()
  if (!root) return
  await router.replace(root)
  for (const path of ancestors) await router.push(path)
  await router.push(route.fullPath)
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * A step back is taken the moment it is asked for, but the history only moves on `popstate`.
 * A second tap in between still sees the parent underneath and steps past it, out of the app —
 * so until the step lands, further moves are ignored. The timer only guards against a step that
 * never lands.
 *
 * Exported for the sheet (MOL-18): closing it takes its own entry away, and closing it together
 * with the screen under it — «Записать» on the search — takes two in one move.
 *
 * The block is a token rather than a flag. The sheet's guard steps over a dead entry from inside
 * the pop of a step already in flight — the chevron's — and takes the block over with `force`: the
 * first step's landing must not lift the block that now belongs to the guard's step, or a second
 * tap on the chevron slipped through between the two pops (adversarial В-3).
 */
let stepping: object | null = null
/** Moves asked for while a step was in flight, made once it has landed (`afterStep`). */
let waiting: (() => void)[] = []

/** Moves `steps` entries back — or forward, if negative — once no other step is in flight. */
export function stepBack(router: Router, steps = 1, force = false): void {
  if (stepping && !force) return
  const token = {}
  stepping = token
  const landed = (): void => {
    window.clearTimeout(timer)
    window.removeEventListener('popstate', landed)
    if (stepping !== token) return
    stepping = null
    const due = waiting
    waiting = []
    for (const run of due) run()
  }
  const timer = window.setTimeout(landed, 1000)
  window.addEventListener('popstate', landed)
  router.go(-steps)
}

/**
 * Runs `run` once no step is in flight — at once when none is (MOL-128). A sheet is told it is
 * closed from inside the pop that closed it, before the step that pop answers has landed: a move
 * made there — the record typed by hand going up once it is finished — was ignored as a second
 * tap. So a move that follows a sheet waits here for the step, not for a clock.
 */
export function afterStep(run: () => void): void {
  if (stepping) waiting.push(run)
  else run()
}

export function useNavigation(): {
  goTab: (to: Tab) => Promise<void>
  goBack: () => Promise<void>
  goUp: () => Promise<void>
} {
  const router = useRouter()
  const route: RouteLocationNormalizedLoaded = useRoute()

  async function goTab(to: Tab): Promise<void> {
    if (stepping) return
    // On a nested screen the tab is tapped from its section, not from nowhere (adversarial Б): up
    // the chain the way «back» goes while an ancestor is underneath, then the tab from there. Read
    // as «from no section», a tab replaced the nested screen and left its section underneath —
    // every round of the shop, «Покупки → запись → Что брать», two more entries before «back»
    // left the app.
    //
    // The tab follows the router's move off this screen, and only that move (review Р-24): a pop
    // eaten by a sheet's guard, or a step that never lands, must not leave it to fire on a later,
    // unrelated move. Without a view transition the router finishes inside its own `popstate`,
    // before the step is counted as landed — so the tab waits for that too (`afterStep`).
    if (!route.meta.tab && backTarget(router, route)?.step) {
      const leaving = route.fullPath
      const stop = router.afterEach((_to, from, failure) => {
        if (from.fullPath !== leaving) return
        stop()
        if (!failure) afterStep(() => void goTab(to))
      })
      stepBack(router)
      afterStep(() => window.setTimeout(stop, 1000))
      return
    }
    const nested = !route.meta.tab
    const move = tabMove(sectionOf(router, route), to, entryBelow(router))
    if (move === 'top' && nested) {
      await router.replace({ name: to })
    } else if (move === 'top') {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    } else if (move === 'back') {
      stepBack(router)
    } else {
      await router[move]({ name: to })
    }
  }

  async function goBack(): Promise<void> {
    if (stepping) return
    // Read at the tap, as the chevron's label is read: only a screen with a chevron gets here.
    await go(backTarget(router, route, useLoginStore().closed))
  }

  async function goUp(): Promise<void> {
    if (stepping) return
    await go(upTarget(router, route))
  }

  async function go(target: BackTarget | null): Promise<void> {
    if (!target) return
    if (target.step) stepBack(router)
    else await router.replace(target.location.fullPath)
  }

  return { goTab, goBack, goUp }
}
